import React, { forwardRef } from "react";
import GaugeMeter from "./GaugeMeter";
import { ChartLineUp } from "@phosphor-icons/react";

// Visually-rich shareable card. Rendered to PNG via html2canvas.
// IMPORTANT: this card is rendered offscreen (or inside a dialog) and then captured.
// All styles must be inline-friendly (no advanced CSS like backdrop-filter that
// html2canvas can't rasterize).
const SnapshotCard = forwardRef(function SnapshotCard(
  { overview, summaryText, news, region, assetClass },
  ref
) {
  if (!overview) return null;
  const score = Math.round(overview.composite || 0);
  const label = overview.label || "Neutral";
  const labelColor = score < 30 ? "#FF3B30" : score < 70 ? "#FFCC00" : "#34C759";
  const t = overview.breakdown?.technical?.score || 0;
  const m = overview.breakdown?.momentum?.score || 0;
  const n = overview.breakdown?.news?.score || 0;
  const v = overview.breakdown?.volatility?.score || 0;
  const ts = new Date().toLocaleString();

  const Bar = ({ label, value }) => {
    const c = value < 30 ? "#FF3B30" : value < 70 ? "#FFCC00" : "#34C759";
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "IBM Plex Mono, monospace", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#A1A1AA", marginBottom: 4 }}>
          <span>{label}</span>
          <span style={{ color: c }}>{Math.round(value)}</span>
        </div>
        <div style={{ height: 5, background: "rgba(255,255,255,0.06)" }}>
          <div style={{ height: "100%", width: `${Math.max(2, value)}%`, background: c }} />
        </div>
      </div>
    );
  };

  return (
    <div
      ref={ref}
      data-testid="snapshot-card"
      style={{
        width: 720,
        background: "#0A0A0A",
        color: "#FFFFFF",
        padding: 32,
        fontFamily: "IBM Plex Sans, sans-serif",
        border: "1px solid rgba(255,255,255,0.10)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* subtle grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 28, height: 28, background: "#FFFFFF", color: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChartLineUp size={16} weight="bold" />
          </div>
          <div>
            <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 700, letterSpacing: "-0.01em", fontSize: 14, lineHeight: 1 }}>
              SENT-I-METER
            </div>
            <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "#A1A1AA", marginTop: 4 }}>
              market mood snapshot · sent-i-meter.com
            </div>
          </div>
        </div>
        <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "#A1A1AA" }}>
          {region} · {assetClass}
        </div>
      </div>

      {/* Body grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, position: "relative" }}>
        {/* Gauge */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <GaugeMeter value={score} label={label} size={260} />
          <div style={{ marginTop: 4, display: "flex", gap: 14, fontFamily: "IBM Plex Mono, monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            <span style={{ color: "#FF3B30" }}>0–30 BEAR</span>
            <span style={{ color: "#FFCC00" }}>30–70 NEUT</span>
            <span style={{ color: "#34C759" }}>70–100 BULL</span>
          </div>
        </div>
        {/* Breakdown + summary */}
        <div>
          <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 700, fontSize: 16, marginBottom: 10 }}>
            How the market feels
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.55, color: "rgba(255,255,255,0.85)", marginBottom: 16, minHeight: 60 }}>
            {summaryText || "Market signals are mixed across asset classes."}
          </div>
          <Bar label="Technical 40%" value={t} />
          <Bar label="Momentum 20%" value={m} />
          <Bar label="News 30%" value={n} />
          <Bar label="Volatility 10%" value={v} />
        </div>
      </div>

      {/* Big score bar at bottom */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
        <div>
          <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#A1A1AA" }}>
            composite score
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 800, fontSize: 56, lineHeight: 1, color: labelColor }}>
              {score}
            </div>
            <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 14, letterSpacing: "0.25em", color: labelColor }}>
              {label.toUpperCase()}
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          {news?.summary && (
            <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11, color: "#A1A1AA" }}>
              <span style={{ color: "#34C759" }}>{news.summary.positive || 0}↑</span>{"  "}
              <span>{news.summary.neutral || 0}=</span>{"  "}
              <span style={{ color: "#FF3B30" }}>{news.summary.negative || 0}↓</span>
              {"  "}news
            </div>
          )}
          <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "#666", marginTop: 6 }}>
            {ts}
          </div>
        </div>
      </div>
    </div>
  );
});

export default SnapshotCard;
